/**
 * Web Platform Storage
 *
 * Persistent device ID storage using IndexedDB (browser-native persistence):
 * - Survives tab/browser close (IndexedDB persists across sessions)
 * - Works when cookies disabled (alternative to localStorage)
 * - Asynchronous API (non-blocking)
 *
 * Features:
 * - Browser-native persistence (IndexedDB API)
 * - Works in incognito mode (session-scoped)
 * - Automatic fallback to memory-only storage on error
 *
 * Created: 2025-01-24 (Phase 2: Platform-Specific Client Storage)
 * @module device/storage.web
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { AnonymousQuotaData } from './storage';

/**
 * IndexedDB schema for device storage
 */
interface DeviceDB extends DBSchema {
  device: {
    key: string;
    value: string;
  };
  quota: {
    key: string;
    value: AnonymousQuotaData;
  };
}

/**
 * Global fallback device ID (memory-only, session-scoped)
 * Used when IndexedDB fails (permissions, private browsing, storage quota)
 */
declare global {
  // eslint-disable-next-line no-var
  var __deviceIdFallback: string | undefined;
  // eslint-disable-next-line no-var
  var __DEV__: boolean;
}

/**
 * Current database version
 *
 * VERSION HISTORY:
 * - v1: Initial release with 'device' store
 * - v2: Added 'quota' store for anonymous quota persistence
 * - v3: Repair migration - fixes corrupted databases from race condition bug
 *       (some users have v2 databases without object stores due to concurrent init race)
 */
const DB_VERSION = 3;

/**
 * Cached database connection promise (singleton pattern)
 *
 * CRITICAL FIX (2025-11-30): Race condition during concurrent IndexedDB initialization
 *
 * Problem: Multiple parts of the app (useUniversalQuota, useTier, useEntitlements) all
 * call getDatabase() on page load simultaneously. Without a singleton:
 * 1. First openDB() triggers upgrade handler (creates 'device', 'quota' stores)
 * 2. Second concurrent openDB() sees version=2, skips upgrade handler
 * 3. But first upgrade HASN'T COMPLETED yet, so stores don't exist
 * 4. Second call tries to access non-existent stores → "object stores was not found" error
 *
 * Solution: Cache the Promise so all concurrent callers wait for the same initialization.
 */
let dbPromise: Promise<IDBPDatabase<DeviceDB>> | null = null;

/**
 * Shared IndexedDB open helper with upgrade handler
 *
 * CRITICAL: All IndexedDB access MUST use this helper to ensure object stores exist.
 * Uses singleton pattern to prevent race conditions during concurrent initialization.
 *
 * @returns Promise<IDBPDatabase<DeviceDB>> - Open database connection
 */
async function getDatabase(): Promise<IDBPDatabase<DeviceDB>> {
  if (!dbPromise) {
    dbPromise = openDB<DeviceDB>('dramadetector', DB_VERSION, {
      upgrade(db, oldVersion) {
        // Version 1: Create device store
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains('device')) {
            db.createObjectStore('device');
          }
        }
        // Version 2: Create quota store (Week 2: Anonymous quota persistence)
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains('quota')) {
            db.createObjectStore('quota');
          }
        }
        // Version 3: Repair migration - fix corrupted databases from race condition
        // Some users have v2 databases where object stores were never created
        // due to concurrent initialization race condition (fixed with singleton pattern)
        if (oldVersion < 3) {
          // Defensive check: create stores if they don't exist (repairs corrupted DBs)
          if (!db.objectStoreNames.contains('device')) {
            db.createObjectStore('device');
            if (__DEV__) {
              console.log('[IndexedDB] Repair: created missing "device" store');
            }
          }
          if (!db.objectStoreNames.contains('quota')) {
            db.createObjectStore('quota');
            if (__DEV__) {
              console.log('[IndexedDB] Repair: created missing "quota" store');
            }
          }
        }
      },
    }).catch((error) => {
      // Reset promise on error to allow retry on next call
      dbPromise = null;
      throw error;
    });
  }
  return dbPromise;
}

/**
 * Get or generate device ID (web platform)
 *
 * Retrieves persistent device ID from IndexedDB.
 * Generates new ID if none exists. Falls back to memory-only storage on error.
 *
 * Performance: 5-10ms (IndexedDB read) or 15-25ms (IndexedDB write on first call)
 *
 * @returns Promise<string> - Persistent device ID (survives tab close)
 *
 * @example
 * ```typescript
 * import { getDeviceId } from '@dramadetector/shared/device/storage';
 *
 * const deviceId = await getDeviceId(); // "web-1706140800000-abc123xyz"
 * ```
 */
export async function getDeviceId(): Promise<string> {
  try {
    const db = await getDatabase();
    let deviceId = await db.get('device', 'device_id');

    if (!deviceId) {
      // Generate stable device ID (web-prefixed for debugging)
      deviceId = `web-${Date.now()}-${Math.random().toString(36).substring(2)}`;
      await db.put('device', deviceId, 'device_id');

      if (__DEV__) {
        console.log('[DEVICE_ID] Generated new device ID (web)');
        console.log('[DEVICE_ID] ID preview:', deviceId.substring(0, 12) + '...');
      }
    } else if (__DEV__) {
      console.log('[DEVICE_ID] Retrieved existing device ID (web)');
      console.log('[DEVICE_ID] ID preview:', deviceId.substring(0, 12) + '...');
    }

    return deviceId;
  } catch (error) {
    // Log error without exposing device ID (PII)
    console.error('[DEVICE_ID] IndexedDB error, falling back to memory-only:', {
      error: error instanceof Error ? error.message : 'unknown',
    });
    return getFallbackDeviceId(); // Memory-only fallback
  }
}

/**
 * Fallback device ID (memory-only, session-scoped)
 *
 * Used when IndexedDB fails (permissions denied, private browsing, storage quota).
 * Persists only for current tab session (lost on tab close).
 *
 * @returns string - Memory-only device ID (session-scoped)
 */
function getFallbackDeviceId(): string {
  if (!globalThis.__deviceIdFallback) {
    globalThis.__deviceIdFallback = `fallback-${Date.now()}-${Math.random().toString(36).substring(2)}`;
    console.warn('[DEVICE_ID] Using memory-only fallback device ID (not persistent)');
  }
  return globalThis.__deviceIdFallback;
}

/**
 * Cache TTL for anonymous quota (7 days in milliseconds)
 *
 * After 7 days, cached quota is considered stale and will be ignored.
 * This ensures users don't see outdated quota if they return after a long absence.
 * The weekly reset period is 7 days, so cache older than that is invalid anyway.
 */
const QUOTA_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Get anonymous quota from IndexedDB (persistent across sessions)
 *
 * Week 2: Client-side quota persistence for anonymous users.
 * Survives page refresh, browser close, and navigation.
 *
 * TTL Validation (2025-11-29): Cache expires after 7 days.
 * This prevents stale quota display for returning users.
 *
 * @returns Promise<AnonymousQuotaData | null> - Cached quota data or null if not found/expired
 *
 * @example
 * ```typescript
 * import { getAnonymousQuota } from '@dramadetector/shared/device/storage';
 *
 * const quota = await getAnonymousQuota();
 * if (quota) {
 *   console.log(`${quota.remaining} of ${quota.limit} analyses remaining`);
 * }
 * ```
 */
export async function getAnonymousQuota(): Promise<AnonymousQuotaData | null> {
  try {
    const db = await getDatabase();
    const quota = await db.get('quota', 'anonymous_quota');

    if (!quota) {
      return null;
    }

    // TTL check: Expire cache after 7 days
    const cacheAge = Date.now() - (quota.lastUpdated || 0);
    const isExpired = cacheAge > QUOTA_CACHE_TTL_MS;

    if (isExpired) {
      if (__DEV__) {
        console.log('[ANONYMOUS_QUOTA] Cache expired (age: %s days), ignoring cached data',
          Math.round(cacheAge / (24 * 60 * 60 * 1000)));
      }
      // Clear expired cache
      await db.delete('quota', 'anonymous_quota');
      return null;
    }

    if (__DEV__) {
      console.log('[ANONYMOUS_QUOTA] Retrieved from IndexedDB:', {
        remaining: quota.remaining,
        limit: quota.limit,
        age: `${Math.round(cacheAge / 1000)}s ago`,
        expiresIn: `${Math.round((QUOTA_CACHE_TTL_MS - cacheAge) / (24 * 60 * 60 * 1000))} days`,
      });
    }

    return quota;
  } catch (error) {
    console.error('[ANONYMOUS_QUOTA] Failed to read from IndexedDB:', {
      error: error instanceof Error ? error.message : 'unknown',
    });
    return null;
  }
}

/**
 * Save anonymous quota to IndexedDB (persistent across sessions)
 *
 * Week 2: Client-side quota persistence for anonymous users.
 * Called after every analysis to keep UI state in sync with backend.
 *
 * @param quota - Quota data from backend (trial.remaining, trial.limit)
 *
 * @example
 * ```typescript
 * import { setAnonymousQuota } from '@dramadetector/shared/device/storage';
 *
 * // After analysis API call
 * if (data?.data?.quota) {
 *   await setAnonymousQuota({
 *     remaining: data.data.quota.remaining,
 *     limit: data.data.quota.limit,
 *     lastUpdated: Date.now(),
 *   });
 * }
 * ```
 */
export async function setAnonymousQuota(quota: AnonymousQuotaData): Promise<void> {
  try {
    const db = await getDatabase();
    await db.put('quota', quota, 'anonymous_quota');

    if (__DEV__) {
      console.log('[ANONYMOUS_QUOTA] Saved to IndexedDB:', {
        remaining: quota.remaining,
        limit: quota.limit,
      });
    }
  } catch (error) {
    console.error('[ANONYMOUS_QUOTA] Failed to write to IndexedDB:', {
      error: error instanceof Error ? error.message : 'unknown',
    });
  }
}

/**
 * Clear anonymous quota from IndexedDB
 *
 * Used for testing and quota reset scenarios.
 * Production: Called when user signs up (convert anonymous → authenticated).
 *
 * @example
 * ```typescript
 * import { clearAnonymousQuota } from '@dramadetector/shared/device/storage';
 *
 * await clearAnonymousQuota(); // Clear cached quota
 * ```
 */
export async function clearAnonymousQuota(): Promise<void> {
  try {
    const db = await getDatabase();
    await db.delete('quota', 'anonymous_quota');

    if (__DEV__) {
      console.log('[ANONYMOUS_QUOTA] Cleared from IndexedDB');
    }
  } catch (error) {
    console.error('[ANONYMOUS_QUOTA] Failed to delete from IndexedDB:', {
      error: error instanceof Error ? error.message : 'unknown',
    });
  }
}

/** Get signed device token from IndexedDB */
export async function getDeviceToken(): Promise<string | null> {
  try {
    const db = await getDatabase();
    const token = await db.get('device', 'device_token');
    return token || null;
  } catch (error) {
    console.warn('[DEVICE_TOKEN] Failed to read from IndexedDB:', {
      error: error instanceof Error ? error.message : 'unknown',
    });
    return null;
  }
}

/** Store signed device token in IndexedDB */
export async function setDeviceToken(token: string): Promise<void> {
  try {
    const db = await getDatabase();
    await db.put('device', token, 'device_token');
    if (__DEV__) {
      console.log('[DEVICE_TOKEN] Saved to IndexedDB');
    }
  } catch (error) {
    console.error('[DEVICE_TOKEN] Failed to write to IndexedDB:', {
      error: error instanceof Error ? error.message : 'unknown',
    });
  }
}

/**
 * Reset device ID (for testing only)
 * DO NOT expose this to production users
 *
 * Clears persistent device ID from IndexedDB and memory fallback.
 * Used for E2E test cleanup and quota reset scenarios.
 *
 * @example
 * ```typescript
 * import { resetDeviceId } from '@dramadetector/shared/device/storage';
 *
 * await resetDeviceId(); // Clear device ID for testing
 * ```
 */
export async function resetDeviceId(): Promise<void> {
  try {
    const db = await getDatabase();
    await db.delete('device', 'device_id');
    await db.delete('quota', 'anonymous_quota'); // Also clear quota
    if (__DEV__) {
      console.log('[DEVICE_ID] Cleared device ID and quota from IndexedDB');
    }
  } catch (error) {
    console.error('[DEVICE_ID] Failed to delete from IndexedDB:', {
      error: error instanceof Error ? error.message : 'unknown',
    });
  }

  // Clear fallback device ID from memory
  if (globalThis.__deviceIdFallback) {
    delete globalThis.__deviceIdFallback;
    if (__DEV__) {
      console.log('[DEVICE_ID] Cleared fallback device ID from memory');
    }
  }
}
